import React from "react";
import { RouteComponentProps } from "react-router";
import { LoadingViewState, RouterView, CardBox } from "../RouterView";
import { getContainer } from "../../ioc/IOCSetup";
import { SimulatedPayoutEntry, SimulatedPayoutResponse } from "../../http/protocol";
import { AffiliatesSimulatePayoutsService } from "../../services/AffiliatesSimulatePayoutsService";

interface State extends LoadingViewState {
  simulation?: SimulatedPayoutResponse;
  running?: boolean;
  failed?: boolean;
}

export class AffiliatesSimulatePayoutsView extends RouterView<RouteComponentProps, State> {
  service: AffiliatesSimulatePayoutsService;

  constructor(props: RouteComponentProps) {
    super(props);
    this.service = getContainer().getAffiliatesSimulatePayoutsService();
    this.state = { loading: true };
  }

  // The projection reruns the monthly payout calculation across every active affiliate, so it is
  // triggered by an explicit button press rather than on page load.
  loadContent(): void {
    return;
  }

  getTitle(): string {
    return "Simulate Payouts";
  }

  private runSimulation = () => {
    this.setState({ running: true, failed: false });
    this.service
      .simulate()
      .then((simulation: SimulatedPayoutResponse) => this.setState({ simulation, running: false }))
      .catch(() => this.setState({ running: false, failed: true }));
  };

  private format(amount: number | undefined, currency: string | undefined): string {
    if (amount === undefined) {
      return "-";
    }
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: currency || "EUR" }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  }

  renderContent(): JSX.Element {
    const { simulation, running, failed } = this.state;
    return (
      <CardBox>
        <div
          className="alert alert-info"
          role="alert"
        >
          Projects the upcoming payout — the current, in-progress month that the next scheduled run will pay — across all active affiliates (revenue share, CPA
          and master override commission). This reruns the payout calculation, so it runs only when you start it. No payouts are executed and nothing is saved.
        </div>
        <button
          className="btn btn-primary mb-3"
          onClick={this.runSimulation}
          disabled={running}
        >
          {running ? "Running…" : "Run simulation"}
        </button>
        {failed && (
          <div
            className="alert alert-danger"
            role="alert"
          >
            Simulation failed. Please try again.
          </div>
        )}
        {simulation && !running && this.renderResult(simulation)}
      </CardBox>
    );
  }

  private renderResult(simulation: SimulatedPayoutResponse): JSX.Element {
    const entries = simulation.entries ?? [];
    return (
      <>
        <p>
          Upcoming payout for <strong>{simulation.month}</strong> — estimate only (excludes carry-over, manual payments and minimum-transfer limits).
        </p>
        {entries.length === 0 ? (
          <p>No projected payouts for this month.</p>
        ) : (
          <div className="table-responsive">
            <table className="table table-striped">
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>ID</th>
                  <th>Payout Currency</th>
                  <th className="text-right">Revenue Share</th>
                  <th className="text-right">CPA</th>
                  <th className="text-right">Master Commission</th>
                  <th className="text-right">Gross Payable</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry: SimulatedPayoutEntry) => (
                  <tr key={entry.affiliateId}>
                    <td>{entry.affiliateName}</td>
                    <td>{entry.affiliateId}</td>
                    <td>{entry.payoutCurrency}</td>
                    <td className="text-right">{this.format(entry.revenueShare, entry.payoutCurrency)}</td>
                    <td className="text-right">{this.format(entry.cpa, entry.payoutCurrency)}</td>
                    <td className="text-right">{this.format(entry.masterCommission, entry.payoutCurrency)}</td>
                    <td className="text-right">{this.format(entry.grossPayable, entry.payoutCurrency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </>
    );
  }
}

export default AffiliatesSimulatePayoutsView;
